import { dripMediumOptions } from 'features/CustomCampaigns/EditCampaign/CampaignSetup'
import { inviteMediumOptionsWithEmail } from 'features/ManageCampaigns/utils/inviteMediumOptions'
import { getCampaignNameFromCategory } from 'main/campaignNamesAndDesc'

const OTA_CAMPAIGN_CATEGORIES = new Set([
  'OTAAfterVisit',
  'OTAJustBooked',
  'OTACheckedOutReview',
  'OTALost3Months',
  'OTALost6Months',
  'OTALost9Months',
  'OTALost12Months',
  'OTALost15Months',
  'OTALost15MonthsPlus',
  'OTACancelled',
  'OTANoShow'
])

const CONSENT_REQUEST_CATEGORIES = new Set([
  'AfterLastVisitConsentRequest',
  'Lost3MonthsConsentRequest',
  'Lost6MonthsConsentRequest',
  'Lost9MonthsConsentRequest',
  'Lost12MonthsConsentRequest',
  'Lost15MonthsConsentRequest'
])

const MARKETING_CAMPAIGN_CATEGORIES = [
  'AfterLastVisit',
  'OTAAfterVisit',
  'AfterLastVisitConsentRequest',
  'Lost3Months',
  'Lost6Months',
  'Lost9Months',
  'Lost12Months',
  'Lost15Months',
  'Lost15MonthsPlus',
  'OTALost3Months',
  'OTALost6Months',
  'OTALost9Months',
  'OTALost12Months',
  'OTALost15Months',
  'OTALost15MonthsPlus'
]

const ALL_REVIEW_CAMPAIGN_CATEGORIES = [
  'CheckedOutReview',
  'OTACheckedOutReview',
  'ReviewReminder1',
  'ReviewReminder2'
]

const TRANSACTIONAL_MEDIUM_CATEGORIES = [
  'JustBooked',
  'OTAJustBooked',
  'Cancelled',
  'OTACancelled',
  'NoShow',
  'OTANoShow'
]

const INVITE_EMAIL_IN_SCOPE_CATEGORIES = [
  ...new Set([
    ...MARKETING_CAMPAIGN_CATEGORIES,
    ...ALL_REVIEW_CAMPAIGN_CATEGORIES,
    ...CONSENT_REQUEST_CATEGORIES,
    ...TRANSACTIONAL_MEDIUM_CATEGORIES
  ])
]

const BULK_MEDIUM_ELIGIBLE_CATEGORIES = INVITE_EMAIL_IN_SCOPE_CATEGORIES.filter(
  category => !CONSENT_REQUEST_CATEGORIES.has(category)
)

export const BULK_MEDIUM_SCOPES = {
  all: 'all',
  ota: 'ota',
  direct: 'direct'
}

const INVITE_MEDIUM_BY_VALUE = {
  0: dripMediumOptions.Text,
  10: dripMediumOptions.Email,
  20: dripMediumOptions.EmailAndText,
  30: dripMediumOptions.TextWithEmailFallback,
  90: 'Both'
}

export function isConsentRequestCategory(category) {
  return CONSENT_REQUEST_CATEGORIES.has(category)
}

export function isBulkMediumEligibleCategory(category) {
  return BULK_MEDIUM_ELIGIBLE_CATEGORIES.includes(category)
}

export function getBulkMediumUpdateCategories(scope) {
  switch (scope) {
    case BULK_MEDIUM_SCOPES.ota:
      return BULK_MEDIUM_ELIGIBLE_CATEGORIES.filter(category =>
        OTA_CAMPAIGN_CATEGORIES.has(category)
      )
    case BULK_MEDIUM_SCOPES.direct:
      return BULK_MEDIUM_ELIGIBLE_CATEGORIES.filter(
        category => !OTA_CAMPAIGN_CATEGORIES.has(category)
      )
    case BULK_MEDIUM_SCOPES.all:
    default:
      return [...BULK_MEDIUM_ELIGIBLE_CATEGORIES]
  }
}

export function getCampaignMedium(campaign) {
  const rawMedium = campaign?.medium ?? campaign?.Medium

  if (rawMedium == null || rawMedium === '') {
    return dripMediumOptions.Text
  }

  if (typeof rawMedium === 'number') {
    return INVITE_MEDIUM_BY_VALUE[rawMedium] ?? dripMediumOptions.Text
  }

  return String(rawMedium)
}

export function getBulkMediumAffectedCampaigns(campaigns, scope) {
  if (!campaigns?.length) {
    return []
  }

  const eligibleCategories = new Set(getBulkMediumUpdateCategories(scope))

  return campaigns.filter(c => eligibleCategories.has(c.category))
}

export function getBulkMediumAffectedCampaignNames(campaigns, scope) {
  return getBulkMediumAffectedCampaigns(campaigns, scope).map(c =>
    getCampaignNameFromCategory(c.category)
  )
}

export const BULK_MEDIUM_MIXED_LABEL = 'Multiple settings'

export function getBulkMediumCurrentValue(campaigns, scope) {
  return getBulkMediumScopeState(campaigns, scope).medium
}

function getBulkMediumSubsetScopeState(campaigns, scope) {
  const scopeCampaigns = getBulkMediumAffectedCampaigns(campaigns, scope)

  if (!scopeCampaigns.length) {
    return {
      medium: dripMediumOptions.Text,
      isMixed: false,
      campaignCount: 0,
      uniqueMediums: []
    }
  }

  const mediums = scopeCampaigns.map(getCampaignMedium)
  const uniqueMediums = [...new Set(mediums)]
  const isMixed = uniqueMediums.length > 1

  if (!isMixed) {
    return {
      medium: uniqueMediums[0],
      isMixed: false,
      campaignCount: scopeCampaigns.length,
      uniqueMediums
    }
  }

  const counts = mediums.reduce((acc, medium) => {
    acc[medium] = (acc[medium] || 0) + 1
    return acc
  }, {})

  return {
    medium: Object.entries(counts).sort(([, aCount], [, bCount]) => bCount - aCount)[0][0],
    isMixed: true,
    campaignCount: scopeCampaigns.length,
    uniqueMediums
  }
}

function getBulkMediumAllScopeState(campaigns) {
  const ota = getBulkMediumSubsetScopeState(campaigns, BULK_MEDIUM_SCOPES.ota)
  const direct = getBulkMediumSubsetScopeState(campaigns, BULK_MEDIUM_SCOPES.direct)
  const bothSubsetsPresent = ota.campaignCount > 0 && direct.campaignCount > 0

  if (bothSubsetsPresent) {
    if (ota.isMixed || direct.isMixed) {
      const allCampaigns = getBulkMediumAffectedCampaigns(campaigns, BULK_MEDIUM_SCOPES.all)
      const uniqueMediums = [...new Set(allCampaigns.map(getCampaignMedium))]

      return {
        medium: null,
        isMixed: true,
        campaignCount: allCampaigns.length,
        uniqueMediums
      }
    }

    if (ota.medium !== direct.medium) {
      return {
        medium: null,
        isMixed: true,
        campaignCount: ota.campaignCount + direct.campaignCount,
        uniqueMediums: [ota.medium, direct.medium]
      }
    }

    return {
      medium: ota.medium,
      isMixed: false,
      campaignCount: ota.campaignCount + direct.campaignCount,
      uniqueMediums: [ota.medium]
    }
  }

  return getBulkMediumSubsetScopeState(campaigns, BULK_MEDIUM_SCOPES.all)
}

export function getBulkMediumScopeState(campaigns, scope) {
  if (scope === BULK_MEDIUM_SCOPES.all) {
    return getBulkMediumAllScopeState(campaigns)
  }

  return getBulkMediumSubsetScopeState(campaigns, scope)
}

export function getBulkMediumDropdownValue(campaigns, scope, mediumOverrides = {}) {
  if (mediumOverrides[scope]) {
    return mediumOverrides[scope]
  }

  const state = getBulkMediumScopeState(campaigns, scope)

  if (state.isMixed && state.medium == null) {
    return ''
  }

  return state.medium
}

export function getBulkMediumDropdownPlaceholder(campaigns, scope, mediumOverrides = {}) {
  if (mediumOverrides[scope]) {
    return undefined
  }

  const state = getBulkMediumScopeState(campaigns, scope)

  if (state.isMixed && state.medium == null) {
    return BULK_MEDIUM_MIXED_LABEL
  }

  return undefined
}

export function getBulkMediumScopeSupportsEmailDesign(campaigns, scope, mediumOverrides = {}) {
  if (mediumOverrides[scope]) {
    return inviteMediumOptionsWithEmail.includes(mediumOverrides[scope])
  }

  const state = getBulkMediumScopeState(campaigns, scope)
  const mediums = state.uniqueMediums?.length
    ? state.uniqueMediums
    : state.medium
      ? [state.medium]
      : []

  return mediums.some(medium => inviteMediumOptionsWithEmail.includes(medium))
}

export function getBulkMediumScopeSummaryText(campaigns, scope, mediumNames) {
  if (scope !== BULK_MEDIUM_SCOPES.all) {
    return null
  }

  const status = getBulkMediumScopeStatus(campaigns)

  if (!status.hasSubsetOverrides || status.ota.isMixed || status.direct.isMixed) {
    return null
  }

  const otaLabel = mediumNames[status.ota.medium] || status.ota.medium
  const directLabel = mediumNames[status.direct.medium] || status.direct.medium

  return `OTA uses ${otaLabel}. Direct uses ${directLabel}.`
}

export function getBulkMediumScopeStatus(campaigns) {
  const all = getBulkMediumScopeState(campaigns, BULK_MEDIUM_SCOPES.all)
  const ota = getBulkMediumScopeState(campaigns, BULK_MEDIUM_SCOPES.ota)
  const direct = getBulkMediumScopeState(campaigns, BULK_MEDIUM_SCOPES.direct)

  const bothSubsetsPresent = ota.campaignCount > 0 && direct.campaignCount > 0
  const bothSubsetsUnified = !ota.isMixed && !direct.isMixed
  const subsetsDiffer =
    bothSubsetsPresent && bothSubsetsUnified && ota.medium !== direct.medium

  return {
    all,
    ota: {
      ...ota,
      differsFromAll: subsetsDiffer
    },
    direct: {
      ...direct,
      differsFromAll: subsetsDiffer
    },
    hasSubsetOverrides: subsetsDiffer,
    allInSync:
      !all.isMixed &&
      !subsetsDiffer &&
      all.campaignCount > 0
  }
}

export function buildBulkMediumChangeNotice({ scope, medium, campaigns, mediumNames }) {
  const status = getBulkMediumScopeStatus(campaigns)
  const mediumLabel = mediumNames[medium] || medium

  if (scope === BULK_MEDIUM_SCOPES.all) {
    const overriddenScopes = []

    if (status.ota.campaignCount > 0 && !status.ota.isMixed && status.ota.medium !== medium) {
      overriddenScopes.push('OTA campaigns')
    }

    if (
      status.direct.campaignCount > 0 &&
      !status.direct.isMixed &&
      status.direct.medium !== medium
    ) {
      overriddenScopes.push('Direct campaigns')
    }

    if (overriddenScopes.length) {
      return `This sets every invite campaign to ${mediumLabel} and replaces current settings for ${overriddenScopes.join(' and ')}.`
    }

    return `This sets every invite campaign at this property to ${mediumLabel}.`
  }

  if (scope === BULK_MEDIUM_SCOPES.ota) {
    return `This sets OTA invite campaigns to ${mediumLabel} only. Direct campaigns keep their current delivery channel.`
  }

  return `This sets direct-booking invite campaigns to ${mediumLabel} only. OTA campaigns keep their current delivery channel.`
}
